import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X, ShoppingCart, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "./lib";

const STATUS_META = {
  ordered:              { label: "Ordered",              chip: "bg-[#a8dadc]/25 text-[#457b9d]" },
  partially_received:   { label: "Partially Received",   chip: "bg-[#FEF0C7] text-[#B54708]" },
  received:             { label: "Received",             chip: "bg-[#12B76A]/12 text-[#0F9256]" },
  cancelled:            { label: "Cancelled",            chip: "bg-[#98A2B3]/20 text-[#475467]" },
};

const VIEWS = [
  { key: "",                     label: "All" },
  { key: "ordered",              label: "Ordered" },
  { key: "partially_received",   label: "Partially Received" },
  { key: "received",             label: "Received" },
  { key: "cancelled",            label: "Cancelled" },
];

export default function Purchases() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (view) params.status = view;
    api.get("/spares/purchases", { params })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, view]);

  return (
    <div className="space-y-4" data-testid="purchases-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Purchases</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} order{rows.length === 1 ? "" : "s"} · vendor POs from your local suppliers.
          </p>
        </div>
        <Link to="/spare-parts/purchases/new"
              data-testid="purchases-add-btn"
              className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> New Purchase
        </Link>
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1 overflow-x-auto">
          {VIEWS.map((v) => (
            <button key={v.key || "all"} onClick={() => setView(v.key)}
                    data-testid={`purchases-view-${v.key || "all"}`}
                    className={`h-8 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                      view === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
                    }`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search PUR #, vendor, invoice…"
                 data-testid="purchases-search"
                 className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="purchases-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <ShoppingCart size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No purchases yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Create your first PO to start tracking vendor orders.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="purchases-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-3 py-2.5">Vendor</th>
                  <th className="px-3 py-2.5">Invoice</th>
                  <th className="px-3 py-2.5 text-right">Lines</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Created</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.ordered;
                  return (
                    <tr key={r.id}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
                        onClick={() => nav(`/spare-parts/purchases/${r.id}`)}
                        data-testid={`purchase-row-${r.id}`}>
                      <td className="px-5 py-3 font-mono font-semibold text-[#1d3557]">{r.number}</td>
                      <td className="px-3 py-3 text-[#1d3557]">{r.vendor_snapshot?.name || "—"}</td>
                      <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{r.invoice_number || "—"}</td>
                      <td className="px-3 py-3 text-right text-[#667085] font-mono">{(r.lines || []).length}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold">{inr(r.totals?.grand_total)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[#667085] text-[11.5px]">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right text-[#a8dadc]"><ChevronRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
